// Copyright © 2019-2026 Focal Systems. All rights reserved.

import { createContext, type ReactNode, useCallback, useContext, useEffect, useMemo, useReducer, useRef } from "react";
import { getAuthToken } from "@/api/client";
import {
  type AssistantState,
  assistantReducer,
  conversationFor,
  historyBefore,
  initialAssistantState,
  isBusy,
} from "@/features/assistant/assistant-reducer";
import { createMockTransport } from "@/features/assistant/mock-transport";
import { loadExpanded, loadThread, saveExpanded, saveThread } from "@/features/assistant/persistence";
import { createSseTransport } from "@/features/assistant/sse-transport";
import type { AssistantTransport, Message, PageContext } from "@/features/assistant/types";

interface AssistantContextValue {
  state: AssistantState;
  pageContext: PageContext | null;
  context: PageContext | null;
  /** False when the question was not taken (blank, or an answer is still running). */
  ask: (question: string) => boolean;
  stop: () => void;
  retry: (answerId: string) => void;
  reset: () => void;
  open: () => void;
  close: () => void;
  toggle: () => void;
  setExpanded: (value: boolean) => void;
  dismissContext: () => void;
  setFeedback: (answerId: string, value: "up" | "down") => void;
  /** Rejects when the answer was never rated or the service refuses; a blank form sends nothing. */
  sendComment: (answerId: string, reasons: string[], comment: string) => Promise<void>;
  retryFeedback: (answerId: string) => void;
  /** Answers from `transport` instead of the provider's own until called with null (the showcase's mock). */
  overrideTransport: (transport: AssistantTransport | null) => void;
}

const AssistantContext = createContext<AssistantContextValue | null>(null);

// The real service when its URL is configured; the scripted mock otherwise (local UI work, showcase).
const defaultTransport = import.meta.env.VITE_ASSISTANT_URL
  ? createSseTransport(import.meta.env.VITE_ASSISTANT_URL, getAuthToken)
  : createMockTransport();

type Rating = "up" | "down";

/** What the service has been told about one answer; calls for an answer run one after another. */
interface FeedbackSync {
  latest: Rating | null;
  sent: Rating | "none" | null;
  id: string | null;
  queue: Promise<void>;
  /** The answer was regenerated or the thread reset: late results must not touch it. */
  cancelled: boolean;
  /** The first rating failed: queued clicks made before that do not resend it. */
  failed: boolean;
}

function newId(): string {
  return crypto.randomUUID();
}

/** Path of the page the latest answer was asked on, to resume context tracking after a reload. */
function lastAnsweredPath(messages: Message[]): string | null {
  const last = [...messages].reverse().find((m) => m.role === "assistant");
  return last?.role === "assistant" ? (last.context?.path ?? null) : null;
}

export function AssistantProvider({
  pageContext,
  transport = defaultTransport,
  ownerId = null,
  children,
}: {
  pageContext: PageContext | null;
  transport?: AssistantTransport;
  /** Who is signed in; the stored thread is only restored for the same person. */
  ownerId?: string | null;
  children: ReactNode;
}) {
  const [state, dispatch] = useReducer(assistantReducer, undefined, () =>
    initialAssistantState(loadThread(ownerId), loadExpanded()),
  );
  const stateRef = useRef(state);
  stateRef.current = state;
  const controllerRef = useRef<AbortController | null>(null);
  const transportOverrideRef = useRef<AssistantTransport | null>(null);
  const feedbackSyncs = useRef(new Map<string, FeedbackSync>());
  const context = state.contextDismissed ? null : pageContext;
  const contextRef = useRef(context);
  contextRef.current = context;

  const run = useCallback(
    async (answerId: string, question: string, answerContext: PageContext | null) => {
      const controller = new AbortController();
      controllerRef.current = controller;
      const history = historyBefore(stateRef.current.messages, answerId);
      let settled = false;
      try {
        const source = transportOverrideRef.current ?? transport;
        for await (const event of source.ask({ question, context: answerContext, history }, controller.signal)) {
          if (controller.signal.aborted) break;
          dispatch({ type: "event", answerId, event });
          // The answer is over at done/error; don't wait for the transport to close its stream
          // before the next question can go.
          if (event.type === "done" || event.type === "error") {
            settled = true;
            break;
          }
        }
        // A stream that ends without `done` still settles.
        if (!settled && !controller.signal.aborted) dispatch({ type: "event", answerId, event: { type: "done" } });
      } catch (error) {
        if (!controller.signal.aborted) {
          const message = error instanceof Error ? error.message : String(error);
          dispatch({ type: "event", answerId, event: { type: "error", message } });
        }
      } finally {
        if (controller.signal.aborted && !settled) dispatch({ type: "stopped", answerId });
        if (controllerRef.current === controller) controllerRef.current = null;
      }
    },
    [transport],
  );

  const ask = useCallback(
    (raw: string) => {
      const question = raw.trim();
      if (!question || isBusy(stateRef.current) || controllerRef.current) return false;
      const answerId = newId();
      const answerContext = contextRef.current;
      // Claim the slot synchronously so a second call in the same tick is ignored.
      controllerRef.current = new AbortController();
      dispatch({ type: "send", userId: newId(), answerId, question, context: answerContext });
      void run(answerId, question, answerContext);
      return true;
    },
    [run],
  );

  const retry = useCallback(
    (answerId: string) => {
      const target = stateRef.current.messages.find((m) => m.id === answerId);
      if (target?.role !== "assistant" || isBusy(stateRef.current) || controllerRef.current) return;
      controllerRef.current = new AbortController();
      const stale = feedbackSyncs.current.get(answerId);
      if (stale) stale.cancelled = true;
      feedbackSyncs.current.delete(answerId);
      dispatch({ type: "retry", answerId });
      void run(answerId, target.question, target.context);
    },
    [run],
  );

  const syncFor = useCallback((answerId: string): FeedbackSync | null => {
    const known = feedbackSyncs.current.get(answerId);
    if (known) return known;
    const answer = stateRef.current.messages.find((m) => m.id === answerId);
    if (answer?.role !== "assistant" || (answer.status !== "done" && answer.status !== "stopped")) return null;
    // Resumed from a stored thread: only a "sent" state says what the service already holds.
    const sync: FeedbackSync = {
      latest: answer.feedback,
      sent: answer.feedbackState === "sent" ? (answer.feedback ?? "none") : null,
      id: answer.feedbackId ?? null,
      queue: Promise.resolve(),
      cancelled: false,
      failed: false,
    };
    feedbackSyncs.current.set(answerId, sync);
    return sync;
  }, []);

  /** Brings the service in line with the latest local rating: one rating, then only changes. */
  const flushFeedback = useCallback(
    async (answerId: string, sync: FeedbackSync) => {
      const source = transportOverrideRef.current ?? transport;
      const target = sync.latest;
      if (sync.cancelled || (sync.id === null && sync.failed)) return;
      if (sync.id === null && target === null) {
        dispatch({ type: "feedbackCleared", answerId });
        return;
      }
      if (sync.id !== null && (target ?? "none") === sync.sent) {
        dispatch({ type: "feedbackSent", answerId, feedbackId: sync.id });
        return;
      }
      dispatch({ type: "feedbackSending", answerId });
      try {
        if (sync.id === null && target !== null) {
          const answer = stateRef.current.messages.find((m) => m.id === answerId);
          const conversation = conversationFor(stateRef.current.messages, answerId);
          sync.id = await source.sendRating({
            requestId: answer?.role === "assistant" ? (answer.requestId ?? null) : null,
            rating: target,
            conversation,
          });
          sync.sent = target;
        } else if (sync.id !== null) {
          await source.sendRatingEvent(sync.id, target ?? "none");
          sync.sent = target ?? "none";
        }
        if (!sync.cancelled && sync.id !== null) dispatch({ type: "feedbackSent", answerId, feedbackId: sync.id });
      } catch {
        if (sync.id === null) sync.failed = true;
        if (!sync.cancelled) dispatch({ type: "feedbackFailed", answerId });
      }
    },
    [transport],
  );

  const queueFeedback = useCallback(
    (answerId: string, sync: FeedbackSync) => {
      sync.queue = sync.queue.then(() => flushFeedback(answerId, sync));
    },
    [flushFeedback],
  );

  const setFeedback = useCallback(
    (answerId: string, value: Rating) => {
      const sync = syncFor(answerId);
      if (!sync) return;
      sync.latest = sync.latest === value ? null : value;
      sync.failed = false;
      dispatch({ type: "feedback", answerId, value });
      queueFeedback(answerId, sync);
    },
    [syncFor, queueFeedback],
  );

  const retryFeedback = useCallback(
    (answerId: string) => {
      const sync = syncFor(answerId);
      if (!sync) return;
      sync.failed = false;
      queueFeedback(answerId, sync);
    },
    [syncFor, queueFeedback],
  );

  const sendComment = useCallback(
    async (answerId: string, reasons: string[], comment: string) => {
      if (reasons.length === 0 && !comment.trim()) return;
      const sync = syncFor(answerId);
      await sync?.queue;
      if (!sync?.id) throw new Error("Rate the answer before adding details");
      await (transportOverrideRef.current ?? transport).sendComment(sync.id, reasons, comment);
    },
    [syncFor, transport],
  );

  const stop = useCallback(() => controllerRef.current?.abort(), []);

  const overrideTransport = useCallback((override: AssistantTransport | null) => {
    transportOverrideRef.current = override;
  }, []);

  const reset = useCallback(() => {
    controllerRef.current?.abort();
    // Free the slot now: a transport slow to honour abort must not block the next question.
    controllerRef.current = null;
    for (const sync of feedbackSyncs.current.values()) sync.cancelled = true;
    feedbackSyncs.current.clear();
    dispatch({ type: "reset" });
  }, []);

  useEffect(function abortOnUnmount() {
    return () => controllerRef.current?.abort();
  }, []);

  useEffect(
    function persistThread() {
      saveThread(state.messages, ownerId);
    },
    [state.messages, ownerId],
  );

  useEffect(
    function persistExpandedPreference() {
      saveExpanded(state.isExpanded);
    },
    [state.isExpanded],
  );

  const pageContextRef = useRef(pageContext);
  pageContextRef.current = pageContext;
  const contextPath = pageContext?.path ?? null;
  const contextLabel = pageContext?.label ?? null;
  // The last page *with* a subject. Pages without one (home, or any page before its title is
  // published, e.g. right after a reload) don't move the conversation anywhere.
  const lastPath = useRef<string | null>(contextPath ?? lastAnsweredPath(state.messages));
  const lastLabel = useRef<string | null>(contextLabel);
  // biome-ignore lint/correctness/useExhaustiveDependencies: path/label are the trigger; the body reads the latest context from a ref
  useEffect(
    function announceContextChange() {
      const current = pageContextRef.current;
      if (!current) return;
      const previousPath = lastPath.current;
      const previousLabel = lastLabel.current;
      lastPath.current = current.path;
      lastLabel.current = current.label;
      if (previousPath === null) return;
      if (previousPath !== current.path) dispatch({ type: "contextChanged", id: newId(), context: current });
      // The new page's title lands a render after its pathname: fix the divider's label then.
      else if (previousLabel !== current.label) dispatch({ type: "contextRelabeled", context: current });
    },
    [contextPath, contextLabel],
  );

  const value = useMemo<AssistantContextValue>(
    () => ({
      state,
      pageContext,
      context,
      ask,
      stop,
      retry,
      reset,
      open: () => dispatch({ type: "open" }),
      close: () => dispatch({ type: "close" }),
      toggle: () => dispatch({ type: "toggle" }),
      setExpanded: (v) => dispatch({ type: "setExpanded", value: v }),
      dismissContext: () => dispatch({ type: "dismissContext" }),
      setFeedback,
      sendComment,
      retryFeedback,
      overrideTransport,
    }),
    [state, pageContext, context, ask, stop, retry, reset, setFeedback, sendComment, retryFeedback, overrideTransport],
  );

  return <AssistantContext.Provider value={value}>{children}</AssistantContext.Provider>;
}

export function useAssistant(): AssistantContextValue {
  const value = useContext(AssistantContext);
  if (!value) throw new Error("useAssistant must be used inside <AssistantProvider>");
  return value;
}

/** For optional consumers (the showcase) that render whether or not the provider is mounted. */
export function useOptionalAssistant(): AssistantContextValue | null {
  return useContext(AssistantContext);
}
